import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useAuth } from "../../../hooks/useAuth";
import {
  useItDashboard,
  useDeveloperWorkbench,
  useDataScientistWorkbench,
} from "../hooks";
import type {
  ItDashboardResponseDto,
  DeveloperWorkbenchResponseDto,
  DataScientistWorkbenchResponseDto,
} from "../it.types";

const ItDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const isHeadView =
    staffSubRole === "CEO" ||
    staffSubRole === "MDCEO" ||
    staffSubRole === "CTO" ||
    staffSubRole === "ERP_HEAD" ||
    staffSubRole === "LLM_HEAD" ||
    staffSubRole === "IT_COMPLIANCE_HEAD" ||
    staffSubRole === "DATA_ENGINEER";
  const isDeveloperView = staffSubRole === "DEVELOPER";
  const isDataScientistView = staffSubRole === "DATA_SCIENTIST";

  const headQuery = useItDashboard({
    enabled: !isDeveloperView && !isDataScientistView,
  });
  const developerQuery = useDeveloperWorkbench({
    enabled: isDeveloperView,
  });
  const dataScientistQuery = useDataScientistWorkbench({
    enabled: isDataScientistView,
  });

  if (isDeveloperView) {
    const { data, isLoading, error } = developerQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="Developer Workbench"
          message="Loading engineering assignments…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="Developer Workbench"
          message="Unable to load IT dashboard. Please try again."
        />
      );
    }

    const workbench: DeveloperWorkbenchResponseDto = {
      tasks: {
        featuresAssigned: data.tasks?.featuresAssigned ?? 0,
        bugsAssigned: data.tasks?.bugsAssigned ?? 0,
        codeReviewsPending: data.tasks?.codeReviewsPending ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="Developer Workbench"
          message="Assigned platform fixes, releases, and operational engineering tasks."
        />

        <SectionCard title="My workload">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Features assigned"
              value={workbench.tasks.featuresAssigned}
            />
            <StatCard
              label="Bugs assigned"
              value={workbench.tasks.bugsAssigned}
            />
            <StatCard
              label="Code reviews pending"
              value={workbench.tasks.codeReviewsPending}
            />
          </div>
        </SectionCard>
      </div>
    );
  }

  if (isDataScientistView) {
    const { data, isLoading, error } = dataScientistQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="Data Scientist Workbench"
          message="Loading model and data assignments…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="Data Scientist Workbench"
          message="Unable to load IT dashboard. Please try again."
        />
      );
    }

    const workbench: DataScientistWorkbenchResponseDto = {
      modelTasks: {
        modelsToTrain: data.modelTasks?.modelsToTrain ?? 0,
        modelsToEvaluate: data.modelTasks?.modelsToEvaluate ?? 0,
        modelsToDeploy: data.modelTasks?.modelsToDeploy ?? 0,
      },
      analyticsTasks: {
        reportsToProduce: data.analyticsTasks?.reportsToProduce ?? 0,
        dashboardsToUpdate: data.analyticsTasks?.dashboardsToUpdate ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="Data Scientist Workbench"
          message="Assigned model, analytics, and data quality tasks."
        />

        <SectionCard title="Models & datasets">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Models to train"
              value={workbench.modelTasks.modelsToTrain}
            />
            <StatCard
              label="Models to evaluate"
              value={workbench.modelTasks.modelsToEvaluate}
            />
            <StatCard
              label="Models to deploy"
              value={workbench.modelTasks.modelsToDeploy}
            />
          </div>
        </SectionCard>

        <SectionCard title="Analytics operations">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Reports to produce"
              value={workbench.analyticsTasks.reportsToProduce}
            />
            <StatCard
              label="Dashboards to update"
              value={workbench.analyticsTasks.dashboardsToUpdate}
            />
          </div>
        </SectionCard>
      </div>
    );
  }

  const { data, isLoading, error } = headQuery;

  if (isLoading) {
    return (
      <PageIntro
        title={isHeadView ? "IT Head Dashboard" : "IT Dashboard"}
        message="Loading IT and platform health overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title={isHeadView ? "IT Head Dashboard" : "IT Dashboard"}
        message="Unable to load IT dashboard. Please try again."
      />
    );
  }

  const dashboard: ItDashboardResponseDto = {
    kpis: {
      openIncidents: data.kpis?.openIncidents ?? 0,
      criticalIncidents: data.kpis?.criticalIncidents ?? 0,
      deploymentsThisMonth: data.kpis?.deploymentsThisMonth ?? 0,
      openBacklogItems: data.kpis?.openBacklogItems ?? 0,
    },
    incidents: {
      p1Incidents: data.incidents?.p1Incidents ?? 0,
      p2Incidents: data.incidents?.p2Incidents ?? 0,
      p3Incidents: data.incidents?.p3Incidents ?? 0,
    },
    deployments: {
      successfulDeployments: data.deployments?.successfulDeployments ?? 0,
      failedDeployments: data.deployments?.failedDeployments ?? 0,
      rollbacksThisMonth: data.deployments?.rollbacksThisMonth ?? 0,
    },
    backlog: {
      featureRequests: data.backlog?.featureRequests ?? 0,
      technicalDebtItems: data.backlog?.technicalDebtItems ?? 0,
      bugsOpen: data.backlog?.bugsOpen ?? 0,
    },
  };

  const { kpis, incidents, deployments, backlog } = dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title={isHeadView ? "IT Head Dashboard" : "IT Dashboard"}
        message="Applications, infrastructure, deployment, and security posture."
      />

      <SectionCard title="IT KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard label="Open incidents" value={kpis.openIncidents} />
          <StatCard
            label="Critical incidents"
            value={kpis.criticalIncidents}
            helpText={`Deployments this month: ${kpis.deploymentsThisMonth}`}
          />
          <StatCard
            label="Open backlog items"
            value={kpis.openBacklogItems}
            helpText="Features, bugs, and technical debt"
          />
        </div>
      </SectionCard>

      <SectionCard title="Incidents & deployments">
        <InfoCard
          title="Incidents"
          body={`P1 incidents: ${incidents.p1Incidents}, P2 incidents: ${incidents.p2Incidents}, P3 incidents: ${incidents.p3Incidents}.`}
        />
        <InfoCard
          title="Deployments"
          body={`Successful deployments: ${deployments.successfulDeployments}, failed deployments: ${deployments.failedDeployments}, rollbacks this month: ${deployments.rollbacksThisMonth}.`}
        />
      </SectionCard>

      <SectionCard title="Backlog overview">
        <InfoCard
          title="Engineering backlog"
          body={`Feature requests: ${backlog.featureRequests}, bugs open: ${backlog.bugsOpen}, technical debt items: ${backlog.technicalDebtItems}.`}
        />
      </SectionCard>
    </div>
  );
};

export default ItDashboardPage;